import dayjs from "dayjs"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"

/** Period options — API values: all | weekly | monthly | yearly */
export const PERIOD_OPTIONS = [
  { value: "all", label: "All" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
]

export const DEFAULT_PERIOD = "yearly"

const KPI_META = [
  {
    key: "total_members",
    title: "Total Members",
    tone: "darkgreen",
    to: "/admin/members",
    module: "members",
  },
  {
    key: "active_cities",
    title: "Active Cities",
    tone: "lightteal",
    to: "/admin/cities",
    module: "cities",
  },
  {
    key: "active_projects",
    title: "Active Projects",
    tone: "purple-custom",
    to: "/admin/projects",
    module: "projects",
  },
  {
    key: "sessions",
    title: "Sessions",
    tone: "brown",
    to: "/admin/sessions",
    module: "sessions",
  },
  {
    key: "inbox_calls",
    title: "Inbox & Calls",
    tone: "pinkbrown",
    to: "/admin/inbox",
    module: "calls",
  },
  {
    key: "rewards_xp",
    title: "Earned XP",
    tone: "darkgreen",
    to: "/admin/rewards",
    module: "rewards",
  },
]

const MODULE_COLORS = {
  projects: "#3b82f6",
  sessions: "#10b981",
  inbox_calls: "#8b5cf6",
  rewards: "#ff822f",
  photography: "#ec4899",
}

const MODULE_PERMISSION = {
  projects: "projects",
  sessions: "sessions",
  inbox_calls: "calls",
  rewards: "rewards",
  photography: "photography",
}

const OVERVIEW_SERIES_META = [
  { key: "members", label: "Members", color: "#3b82f6" },
  { key: "projects", label: "Projects", color: "#10b981" },
  { key: "sessions", label: "Sessions", color: "#8b5cf6" },
]

export const formatStatValue = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  if (Math.abs(num) >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 2)}M`
  }
  return num.toLocaleString("en-US")
}

export const formatGrowthDelta = value => {
  const num = Number(value)
  if (!Number.isFinite(num) || num === 0) return "0"
  const formatted = formatStatValue(Math.abs(num))
  return num > 0 ? `+${formatted}` : `-${formatted}`
}

export const mapKpiCards = (stats = {}, growth = {}) =>
  KPI_META.map(meta => ({
    ...meta,
    value: formatStatValue(stats[meta.key]),
    delta: formatGrowthDelta(growth[meta.key]),
  }))

export const mapPlatformOverview = (result = {}, period = DEFAULT_PERIOD) => {
  const labels = (result.labels || []).map(label => {
    const parsed = dayjs(label)
    if (!parsed.isValid()) return label
    if (period === "all") {
      return parsed.format("YYYY")
    }
    if (period === "yearly") {
      return parsed.format("MMM")
    }
    return parsed.format("MMM D")
  })

  const series = OVERVIEW_SERIES_META.map(meta => ({
    ...meta,
    values: Array.isArray(result[meta.key])
      ? result[meta.key].map(value => Number(value) || 0)
      : [],
  }))

  return { labels, series }
}

export const mapModuleUsage = (result = {}) =>
  (result.modules || []).map(module => ({
    key: module.key,
    label: module.name || module.key,
    percent: Number(module.percentage) || 0,
    total: module.total,
    color: MODULE_COLORS[module.key] || "#6e6e6e",
    permissionModule: MODULE_PERMISSION[module.key] || module.key,
  }))

export const MEMBERS_BY_CITY_COLORS = [
  "#8b5cf6",
  "#ff822f",
  "#10b981",
  "#ec4899",
  "#3b82f6",
]

export const mapMembersByCity = (result = {}) => {
  const topCities = (result.top_cities || []).slice(0, 5)
  const otherCount = Number(result.other_cities?.members_count) || 0
  const topCitiesTotal = topCities.reduce(
    (sum, item) => sum + (Number(item.members_count) || 0),
    0,
  )
  const total = topCitiesTotal + otherCount

  const formatPercent = value =>
    total > 0 ? Math.round((value / total) * 1000) / 10 : 0

  const segments = topCities.map((item, index) => ({
    id: item.city_id,
    name: item.city || "--",
    percent: formatPercent(Number(item.members_count) || 0),
    color: MEMBERS_BY_CITY_COLORS[index % MEMBERS_BY_CITY_COLORS.length],
  }))

  return {
    totalMembers: total,
    segments,
    otherPercent: formatPercent(otherCount),
  }
}

export const mapActiveCities = (result = {}) =>
  (result.data || []).map(item => ({
    id: item.id,
    name: item.city || "--",
    state: item.state || "--",
    country: item.country || "",
    members: formatStatValue(item.total_members),
    projects: formatStatValue(item.active_projects),
    addedDate: item.created_at
      ? dayjs(item.created_at).format("MMMM D, YYYY")
      : "--",
    thumbnail: item.thumbnail_path_url || item.cover_media_url || "",
  }))

const ACTIVITY_DOT_COLORS = ["blue", "green", "purple", "orange"]

const ACTIVITY_DOT_BY_LOG = {
  loggedIn: "green",
  loggedOut: "orange",
  modelActivity: "blue",
  created: "green",
  updated: "blue",
  deleted: "orange",
}

const formatRelativeTime = dateString => {
  if (!dateString) return ""
  const date = dayjs(dateString)
  if (!date.isValid()) return ""

  const diffSec = Math.max(0, dayjs().diff(date, "second"))
  if (diffSec < 60) return "Just now"

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`
  }

  return date.format("MMM D, YYYY")
}

/** Strip HTML tags and decode entities → plain text for activity labels. */
const stripHtml = value => {
  const input = String(value || "").trim()
  if (!input) return ""

  if (typeof document !== "undefined") {
    const el = document.createElement("div")
    el.innerHTML = input
    return (el.textContent || el.innerText || "").replace(/\s+/g, " ").trim()
  }

  return input
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim()
}

const resolveActivityActor = properties => {
  if (!properties || typeof properties !== "object") return ""
  return getUserFullName(properties) || ""
}

/** Map `/api/v1/admin/activity-logs` rows → Recent Activity ListItem props. */
export const mapRecentActivity = (result, limit = 5) => {
  const rows = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : []

  return rows.slice(0, limit).map((log, index) => {
    const properties = log?.properties || {}
    const eventKey = properties.event || log?.log_name
    const actor = stripHtml(resolveActivityActor(properties))
    const description = stripHtml(
      log?.description || log?.log_name || "Activity",
    )

    return {
      id: log.id ?? `activity-${index}`,
      title: actor ? `${actor} — ${description}` : description,
      time: formatRelativeTime(log.created_at),
      dotColor:
        ACTIVITY_DOT_BY_LOG[eventKey] ||
        ACTIVITY_DOT_BY_LOG[log?.log_name] ||
        ACTIVITY_DOT_COLORS[index % ACTIVITY_DOT_COLORS.length],
    }
  })
}

export const ACTIVE_COLLABORATORS_LIMIT = 6
export const ACTIVE_COLLABORATORS_WITH = ["currentRoles.roleTypes"]

const formatCollaboratorName = user =>
  getUserFullName(user, { fallback: "—" })

const roleTypeName = role => {
  const types = role?.role_types || role?.roleTypes || []
  const type = types[0]
  return String(type?.public_name || type?.name || "").trim()
}

const pickCollaboratorRole = user => {
  const roles = user?.current_roles || user?.currentRoles || []
  if (!Array.isArray(roles) || roles.length === 0) return "—"

  const ongoing = roles.find(role => role?.is_ongoing)
  const fromOngoing = roleTypeName(ongoing)
  if (fromOngoing) return fromOngoing

  const fromAny = roles.map(roleTypeName).find(Boolean)
  if (fromAny) return fromAny

  return "—"
}

/**
 * Map members list payload → Active Collaborators rows.
 * Supports result.data[] shaped like GET /api/v1/members/:cityId /
 * GET /api/v1/admin/members with with[]=currentRoles.roleTypes.
 */
export const mapActiveCollaborators = (
  result,
  limit = ACTIVE_COLLABORATORS_LIMIT,
) => {
  const rows = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : []

  return rows.slice(0, limit).map((user, index) => ({
    id: user?.id || `collaborator-${index}`,
    name: formatCollaboratorName(user),
    role: pickCollaboratorRole(user),
    avatarSrc: getUserAvatarUrl(user) || "",
  }))
}
